/* Money formatting and coin breakdown.
 *
 * The server is the only thing that does money ARITHMETIC -- this file
 * formats. Amounts cross the wire as integer cents and stay integers here;
 * there is deliberately no cents-to-float helper, because the moment one
 * exists someone will sum with it.
 */

export function formatCents(cents: number): string {
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const dollars = Math.floor(abs / 100);
  const remainder = abs % 100;
  const body = `$${dollars.toLocaleString("en-US")}.${String(remainder).padStart(2, "0")}`;
  return negative ? `-${body}` : body;
}

/** Basis points -> a human percentage. 3333 -> "33.33%", 3000 -> "30%". */
export function formatBp(bp: number): string {
  const pct = bp / 100;
  return `${Number.isInteger(pct) ? pct : pct.toFixed(2)}%`;
}

export type CoinKind = "quarter" | "dime" | "nickel" | "penny";
export type BillKind = "hundred" | "fifty" | "twenty" | "ten" | "five" | "one";
export type CashKind = BillKind | CoinKind;

export const COIN_VALUES: Record<CoinKind, number> = {
  quarter: 25,
  dime: 10,
  nickel: 5,
  penny: 1,
};

export const BILL_VALUES: Record<BillKind, number> = {
  hundred: 10000,
  fifty: 5000,
  twenty: 2000,
  ten: 1000,
  five: 500,
  one: 100,
};

export const CASH_VALUES: Record<CashKind, number> = { ...BILL_VALUES, ...COIN_VALUES };

/* Biggest first. Every function here walks this order, so there is exactly one
 * place that decides what a dollar outranks. */
export const CASH_ORDER: CashKind[] = [
  "hundred", "fifty", "twenty", "ten", "five", "one",
  "quarter", "dime", "nickel", "penny",
];

export const CASH_LABELS: Record<CashKind, string> = {
  hundred: "hundred-dollar bill",
  fifty: "fifty-dollar bill",
  twenty: "twenty-dollar bill",
  ten: "ten-dollar bill",
  five: "five-dollar bill",
  one: "one-dollar bill",
  quarter: "quarter",
  dime: "dime",
  nickel: "nickel",
  penny: "penny",
};

export function isBill(kind: CashKind): kind is BillKind {
  return kind in BILL_VALUES;
}

/** What a denomination is called on a slider: "$5", "25¢". */
export function cashLabel(kind: CashKind): string {
  const value = CASH_VALUES[kind];
  return value >= 100 ? `$${value / 100}` : `${value}¢`;
}

export type CashPile = { kind: CashKind; count: number };
export type CashCounts = Partial<Record<CashKind, number>>;

/* Greedy breakdown into real US money.
 *
 * This is the whole point of the jar: $7.50 is not a number, it is a five, two
 * ones and two quarters you could hold. Greedy is OPTIMAL for US
 * denominations -- it really is the fewest pieces, not merely a plausible
 * pile -- and it is also how a person counts money out of a till.
 */
export function cashBreakdown(cents: number): CashPile[] {
  let remaining = Math.max(0, Math.floor(cents));
  const piles: CashPile[] = [];
  for (const kind of CASH_ORDER) {
    const count = Math.floor(remaining / CASH_VALUES[kind]);
    if (count > 0) {
      piles.push({ kind, count });
      remaining -= count * CASH_VALUES[kind];
    }
  }
  return piles;
}

/** Total notes and coins in a breakdown, so a drawing can decide when a pile
 *  has stopped being countable and should become a stack with a number on it. */
export function cashCount(cents: number): number {
  return cashBreakdown(cents).reduce((sum, p) => sum + p.count, 0);
}

/** Plain-language count, for a screen reader and for the youngest tier. */
export function describeCash(cents: number): string {
  const piles = cashBreakdown(cents);
  if (!piles.length) return "nothing in here yet";
  return piles
    .map((p) => `${p.count} ${CASH_LABELS[p.kind]}${p.count === 1 ? "" : "s"}`)
    .join(", ");
}

/** The fewest-pieces breakdown as a count per denomination. */
export function cashCounts(cents: number): CashCounts {
  const counts: CashCounts = {};
  for (const pile of cashBreakdown(cents)) counts[pile.kind] = pile.count;
  return counts;
}

/** The same amount in ones and change -- how allowance actually arrives. */
export function countsAsOnes(cents: number): CashCounts {
  const total = Math.max(0, Math.floor(cents));
  const ones = Math.floor(total / 100);
  return { ...(ones > 0 ? { one: ones } : {}), ...cashCounts(total % 100) };
}

export function countsTotal(counts: CashCounts): number {
  return CASH_ORDER.reduce(
    (sum, kind) => sum + (counts[kind] ?? 0) * CASH_VALUES[kind], 0);
}

/* Re-count the SAME total with more (or fewer) of one denomination.
 *
 * The counting toy behind a tapped jar: drag the $1 row up and the fives and
 * quarters rearrange themselves so the total never moves. That invariant is
 * the entire lesson -- $7.50 has many shapes and exactly one value -- so it is
 * enforced here rather than trusted to the caller.
 *
 * Denominations ABOVE the one being changed are held where the child put them;
 * everything below is re-solved greedily out of what is left. Pennies can
 * absorb any remainder, so a solution always exists and the result sums to
 * `total` exactly. `next` is clamped at both ends: up, to what the remainder
 * can afford, which stops a slider promising eight fives out of $7.50; and
 * down, only for the penny row, which has nothing under it to take the slack.
 */
export function recount(
  total: number,
  counts: CashCounts,
  kind: CashKind,
  next: number,
): CashCounts {
  const at = CASH_ORDER.indexOf(kind);
  if (at < 0) return counts;

  const out: CashCounts = {};
  let remaining = Math.max(0, Math.floor(total));

  for (const above of CASH_ORDER.slice(0, at)) {
    const held = Math.min(counts[above] ?? 0, Math.floor(remaining / CASH_VALUES[above]));
    if (held > 0) {
      out[above] = held;
      remaining -= held * CASH_VALUES[above];
    }
  }

  const value = CASH_VALUES[kind];
  const taken = Math.max(0, Math.min(Math.floor(next), Math.floor(remaining / value)));
  if (taken > 0) {
    out[kind] = taken;
    remaining -= taken * value;
  }

  for (const below of CASH_ORDER.slice(at + 1)) {
    const count = Math.floor(remaining / CASH_VALUES[below]);
    if (count > 0) {
      out[below] = count;
      remaining -= count * CASH_VALUES[below];
    }
  }

  /* The smallest denomination is the one that cannot be lowered: there is
     nothing beneath a penny to absorb what it leaves behind, so dragging that
     row down would otherwise drop cents off the total. Anything still unspent
     goes back onto it -- the penny row snaps straight back, which is the truth
     (you cannot make $12.34 out of one penny) rather than a silent loss. The
     smallest unit is one cent, so this always lands exactly. */
  const last = CASH_ORDER[CASH_ORDER.length - 1];
  if (remaining > 0) out[last] = (out[last] ?? 0) + remaining;

  return out;
}

/** The most of one denomination this total could be made of. Drives the top
 *  end of each slider, so a $7.50 jar offers at most one five. */
export function maxOf(total: number, kind: CashKind): number {
  return Math.floor(Math.max(0, total) / CASH_VALUES[kind]);
}

/* How a register row's amount should read.
 *
 * A jar-to-jar move nets to exactly zero for the child -- correct
 * bookkeeping, and a terrible register line: "Moved money +$0.00" reads as a
 * bug to an adult and as nothing at all to a seven-year-old. So a zero-delta
 * transfer shows the amount that actually moved, unsigned and in neutral ink,
 * because nothing was gained or lost. Everything else keeps its sign.
 */
export function amountReading(row: {
  kind: string;
  deltaCents: number;
  amountCents: number;
}): { text: string; tone: string } {
  if (row.kind === "bucket_transfer" && row.deltaCents === 0) {
    return { text: formatCents(row.amountCents), tone: "var(--ink-2)" };
  }
  return {
    text: `${row.deltaCents >= 0 ? "+" : ""}${formatCents(row.deltaCents)}`,
    tone: row.deltaCents < 0 ? "var(--bad)" : "var(--save-deep)",
  };
}
